import { useEffect, MutableRefObject } from 'react';
import { IChartApi, MouseEventParams } from 'lightweight-charts';
import { throttle } from 'lodash';
import { PerpetualDataPoint } from '../market-data.types';
import { SeriesState } from '../series-reducer';
import { PERPETUAL_ID } from '../market-data.constants';

type ICrosshairMove = {
  chartRef: MutableRefObject<IChartApi | undefined>;
  setHoverData: (data: PerpetualDataPoint | null) => void;
  series: SeriesState;
};

export const useCrosshairMove = ({ chartRef, setHoverData, series }: ICrosshairMove) => {
  useEffect(() => {
    if (!chartRef.current) return;

    const handleCrosshairMove = (param: MouseEventParams) => {
      if (!param || !param.time) return;

      const perp = series[PERPETUAL_ID];
      if (!perp) return;

      const dataMap = param.seriesData;
      if (!dataMap) return;

      const raw = dataMap.get(perp.seriesApi);
      if (raw == null) return;

      if (typeof raw !== 'number' && 'open' in raw && 'high' in raw) {
        const c = raw as any;
        setHoverData({
          time: param.time as number,
          open: c.open,
          high: c.high,
          low: c.low,
          value: c.close,
        });
        return;
      }

      if (typeof raw !== 'number' && 'value' in raw) {
        setHoverData({
          time: param.time as number,
          value: (raw as { value: number }).value,
        });
        return;
      }

      if (typeof raw === 'number') {
        setHoverData({ time: param.time as number, value: raw });
        return;
      }
    };

    const throttledMove = throttle(handleCrosshairMove, 150, {
      leading: true,
      trailing: true,
    });

    chartRef.current.subscribeCrosshairMove(throttledMove);

    return () => {
      chartRef.current?.unsubscribeCrosshairMove(throttledMove);
      throttledMove.cancel();
    };
  }, [chartRef, series, setHoverData]);
};
